import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link2, Unlink, Loader2, Globe, Camera } from 'lucide-react';
import {
  gymSocialBtnPrimary,
  gymSocialBtnSecondary,
  gymSocialCard,
  gymSocialMutedText,
  gymSocialTitleText,
} from '../gymPageTheme';

export default function GymSocialAccountPanel({
  status,
  connecting,
  onConnectMeta,
  onConnectInstagram,
  onDisconnect,
}) {
  const { t } = useTranslation();

  if (!status) {
    return null;
  }

  return (
    <div className={`${gymSocialCard} p-6 space-y-4 w-full max-w-full`}>
      {status.connected ? (
        <>
          <p className="font-medium text-green-700 dark:text-green-400">
            {t('gym.social.connected')}
          </p>
          {status.fb_linked && !status.ig_linked && (
            <div className="space-y-3">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                {t('gym.social.igNotLinkedHint')}
              </p>
              {status.instagram_oauth_configured && (
                <button
                  type="button"
                  disabled={connecting}
                  onClick={onConnectInstagram}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100 dark:border-[#7c5ce0] dark:bg-gray-800 dark:text-[#c4b5fd] dark:hover:bg-gray-700/40 disabled:opacity-50"
                >
                  {connecting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Camera className="h-4 w-4" />
                  )}
                  {t('gym.social.connectInstagram')}
                </button>
              )}
            </div>
          )}
          <ul className={`space-y-2 text-sm ${gymSocialMutedText}`}>
            {status.account?.fb_page_name && (
              <li className="flex items-center gap-2">
                <Globe className="h-4 w-4 shrink-0" aria-hidden />
                {status.account.fb_page_name}
              </li>
            )}
            {status.account?.ig_username && (
              <li className="flex items-center gap-2">
                <Camera className="h-4 w-4 shrink-0" aria-hidden />@{status.account.ig_username}
              </li>
            )}
          </ul>
          {!status.fb_linked && status.instagram_oauth_configured && (
            <button
              type="button"
              disabled={connecting}
              onClick={onConnectMeta}
              className={gymSocialBtnSecondary}
            >
              {connecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4" />
              )}
              {t('gym.social.connectMeta')}
            </button>
          )}
          <button
            type="button"
            onClick={onDisconnect}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-red-300 text-red-700 hover:bg-red-50 dark:border-red-400/60 dark:text-red-300 dark:hover:bg-red-900/30"
          >
            <Unlink className="h-4 w-4" />
            {t('gym.social.disconnect')}
          </button>
        </>
      ) : (
        <>
          <p className={`text-sm ${gymSocialMutedText}`}>{t('gym.social.notConnected')}</p>
          <div
            className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 text-sm space-y-2 dark:border-slate-600 dark:bg-slate-800/50"
            role="note"
          >
            <p className={`font-medium ${gymSocialTitleText}`}>{t('gym.social.requirementsTitle')}</p>
            <p className={gymSocialMutedText}>{t('gym.social.requirementsIntro')}</p>
            <ul className={`list-disc pl-5 space-y-1 ${gymSocialMutedText}`}>
              <li>{t('gym.social.requirementsIgOnly')}</li>
              <li>{t('gym.social.requirementsFbPath')}</li>
            </ul>
            <p className={`text-xs ${gymSocialMutedText}`}>{t('gym.social.requirementsIgProf')}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {status.instagram_oauth_configured && (
              <button
                type="button"
                disabled={connecting}
                onClick={onConnectInstagram}
                className={gymSocialBtnPrimary}
              >
                {connecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
                {t('gym.social.connectInstagramOnly')}
              </button>
            )}
            <button
              type="button"
              disabled={connecting || !status.meta_configured}
              onClick={onConnectMeta}
              className={
                status.instagram_oauth_configured ? gymSocialBtnSecondary : gymSocialBtnPrimary
              }
            >
              {connecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="h-4 w-4" />
              )}
              {t('gym.social.connectMeta')}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
